import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { theme } from '../theme';

const OZ_ORANGE = '#F57C1F';
const OZ_BLUE = '#29A8E0';

const inputStyle = {
  width: '100%', padding: '12px 14px', borderRadius: '8px',
  border: `1px solid ${theme.border}`, fontSize: '0.95rem',
  outline: 'none', marginBottom: '12px', boxSizing: 'border-box',
};

const labelStyle = { fontSize: '0.82rem', fontWeight: '600', color: theme.navy, marginBottom: '6px', display: 'block' };

const btnStyle = {
  width: '100%', padding: '13px', borderRadius: '8px',
  background: OZ_ORANGE, color: '#fff', fontWeight: '700',
  border: 'none', fontSize: '1rem', cursor: 'pointer', marginTop: '8px',
};

const ClinicSignupPage = ({ API_BASE, onSignupSuccess, onBackToLogin }) => {
  const [step, setStep] = useState('form');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });

  const [form, setForm] = useState({
    clinicName: '', ownerName: '', ownerEmail: '', ownerMobile: '', password: '', confirmPassword: '',
  });

  const [signupResult, setSignupResult] = useState(null);
  const [mobileOtp, setMobileOtp] = useState('');
  const [emailOtp, setEmailOtp] = useState('');
  const [mobileVerified, setMobileVerified] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [mobileResendIn, setMobileResendIn] = useState(0);
  const [emailResendIn, setEmailResendIn] = useState(0);

  // 2Factor.in Phone OTP signup flow state
  const [signupMethod, setSignupMethod] = useState('sms');   // 'whatsapp' | 'sms'
  const [phoneStep,    setPhoneStep]    = useState('enter-phone'); // 'enter-phone' | 'enter-otp' | 'details'
  const [phoneOtp,     setPhoneOtp]     = useState('');

  const sendPhoneOtpSms = async () => {
    setMsg({ type: '', text: '' });
    if (!/^\d{10}$/.test(form.ownerMobile.replace(/\D/g, '').slice(-10))) {
      return setMsg({ type: 'error', text: 'Valid 10-digit mobile dalo' });
    }
    setLoading(true);
    try {
      await axios.post(`${API_BASE}/saas-auth/phone/send-otp`, {
        mobile: form.ownerMobile, purpose: 'signup',
      });
      setPhoneStep('enter-otp');
      setMsg({ type: 'success', text: `📞 Call aa rahi hai +91-${form.ownerMobile} pe — OTP suno` });
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.error || 'Failed to send OTP' });
    }
    setLoading(false);
  };

  const verifyPhoneOtpSms = async () => {
    if (phoneOtp.length < 4) return setMsg({ type: 'error', text: 'OTP dalo (4-6 digits)' });
    setPhoneStep('details');
    setMsg({ type: 'success', text: 'OTP entered. Ab clinic details bharo.' });
  };

  const submitPhoneSignup = async () => {
    setMsg({ type: '', text: '' });
    if (!form.clinicName || !form.ownerName || !form.ownerEmail || !form.password) {
      return setMsg({ type: 'error', text: 'Sabhi fields bharna zaroori hai' });
    }
    if (form.password.length < 8) return setMsg({ type: 'error', text: 'Password minimum 8 characters' });
    if (form.password !== form.confirmPassword) return setMsg({ type: 'error', text: 'Passwords match nahi kar rahe' });

    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/saas-auth/phone/signup`, {
        mobile: form.ownerMobile, otp: phoneOtp,
        clinicName: form.clinicName, ownerName: form.ownerName,
        ownerEmail: form.ownerEmail, password: form.password,
      });
      setSignupResult(res.data);
      setStep('verify');
      setMsg({ type: 'success', text: 'Account ban gaya! Mobile SMS OTP se verified hai.' });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Signup failed' });
    }
    setLoading(false);
  };

  useEffect(() => {
    if (mobileResendIn <= 0) return;
    const t = setTimeout(() => setMobileResendIn(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [mobileResendIn]);

  useEffect(() => {
    if (emailResendIn <= 0) return;
    const t = setTimeout(() => setEmailResendIn(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [emailResendIn]);

  const resendOtp = async (type) => {
    try {
      const body = type === 'mobile' ? { type, mobile: form.ownerMobile } : { type, email: form.ownerEmail };
      await axios.post(`${API_BASE}/saas-auth/resend-otp`, body);
      if (type === 'mobile') setMobileResendIn(60);
      else setEmailResendIn(60);
      setMsg({ type: 'success', text: `${type === 'mobile' ? 'Mobile' : 'Email'} OTP resent` });
    } catch (err) {
      const data = err.response?.data;
      if (data?.retryAfterSec) {
        if (type === 'mobile') setMobileResendIn(data.retryAfterSec);
        else setEmailResendIn(data.retryAfterSec);
      }
      setMsg({ type: 'error', text: data?.error || 'Resend failed' });
    }
  };

  const update = (k, v) => setForm({ ...form, [k]: v });

  const submitSignup = async () => {
    setMsg({ type: '', text: '' });
    if (!form.clinicName || !form.ownerName || !form.ownerEmail || !form.ownerMobile || !form.password) {
      return setMsg({ type: 'error', text: 'Sabhi fields bharna zaroori hai' });
    }
    if (form.password.length < 8) return setMsg({ type: 'error', text: 'Password minimum 8 characters' });
    if (form.password !== form.confirmPassword) return setMsg({ type: 'error', text: 'Passwords match nahi kar rahe' });
    if (!/^\d{10}$/.test(form.ownerMobile.replace(/\D/g, '').slice(-10))) {
      return setMsg({ type: 'error', text: 'Valid 10-digit mobile dalo' });
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/saas-auth/signup`, {
        clinicName: form.clinicName,
        ownerName: form.ownerName,
        ownerEmail: form.ownerEmail,
        ownerMobile: form.ownerMobile,
        password: form.password,
      });
      setSignupResult(res.data);
      setStep('verify');
      setMsg({ type: 'success', text: 'Account ban gaya! Ab OTP verify karo.' });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Signup failed' });
    }
    setLoading(false);
  };

  const verifyMobile = async () => {
    if (!mobileOtp) return setMsg({ type: 'error', text: 'Mobile OTP dalo' });
    setLoading(true);
    try {
      await axios.post(`${API_BASE}/saas-auth/verify-mobile-otp`, {
        mobile: form.ownerMobile, otp: mobileOtp,
      });
      setMobileVerified(true);
      setMsg({ type: 'success', text: 'Mobile verified ✓' });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'OTP invalid' });
    }
    setLoading(false);
  };

  const verifyEmail = async () => {
    if (!emailOtp) return setMsg({ type: 'error', text: 'Email OTP dalo' });
    setLoading(true);
    try {
      await axios.post(`${API_BASE}/saas-auth/verify-email-otp`, {
        email: form.ownerEmail, otp: emailOtp,
      });
      setEmailVerified(true);
      setMsg({ type: 'success', text: 'Email verified ✓' });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'OTP invalid' });
    }
    setLoading(false);
  };

  const finishSignup = () => {
    if (onSignupSuccess) onSignupSuccess({ ...signupResult, email: form.ownerEmail });
  };

  return (
    <div style={{
      minHeight: '100vh', background: 'linear-gradient(135deg, #FFF6EC 0%, #E6F4FB 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
    }}>
      <div style={{
        background: '#fff', padding: '36px 32px', borderRadius: '16px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.08)', width: '100%', maxWidth: '460px',
      }}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{ fontSize: '1.8rem', fontWeight: '800', color: OZ_ORANGE, letterSpacing: '0.02em' }}>ORALZY</div>
          <div style={{ fontSize: '0.85rem', color: theme.textSubtle, marginTop: '4px' }}>
            {step === 'form' ? 'Naye Clinic Ka Account Banao' : 'Mobile & Email Verify Karo'}
          </div>
        </div>

        {msg.text && (
          <div style={{
            padding: '10px 14px', borderRadius: '8px', marginBottom: '14px', fontSize: '0.88rem',
            background: msg.type === 'error' ? '#FFE4E4' : '#E4FFE4',
            color: msg.type === 'error' ? '#B00020' : '#0A7B3D',
            border: `1px solid ${msg.type === 'error' ? '#FFB4B4' : '#B4FFB4'}`,
          }}>{msg.text}</div>
        )}

        {step === 'form' && (
          <>
            {/* Signup method tabs */}
            <div style={{display:'flex', marginBottom:'18px', background:'#F5F5F5', borderRadius:8, padding:3, gap:3}}>
              <button onClick={() => setSignupMethod('sms')}
                style={{flex:1, padding:'9px', border:'none', cursor:'pointer', fontWeight:600, fontSize:'0.82rem', borderRadius:6,
                  background: signupMethod === 'sms' ? '#fff' : 'transparent',
                  color: signupMethod === 'sms' ? theme.navy : theme.textMuted,
                  boxShadow: signupMethod === 'sms' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                }}>📞 Voice Call OTP</button>
              <button onClick={() => { setSignupMethod('whatsapp'); setPhoneStep('enter-phone'); }}
                style={{flex:1, padding:'9px', border:'none', cursor:'pointer', fontWeight:600, fontSize:'0.82rem', borderRadius:6,
                  background: signupMethod === 'whatsapp' ? '#fff' : 'transparent',
                  color: signupMethod === 'whatsapp' ? theme.navy : theme.textMuted,
                  boxShadow: signupMethod === 'whatsapp' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                }}>💬 WhatsApp OTP</button>
            </div>

            {/* SMS (2Factor.in) signup flow */}
            {signupMethod === 'sms' && phoneStep === 'enter-phone' && (
              <>
                <label style={labelStyle}>Mobile Number * (10 digit)</label>
                <input style={inputStyle} value={form.ownerMobile}
                  onChange={e => update('ownerMobile', e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="9988776655" maxLength={10} />
                <div style={{fontSize:'0.78rem', color:theme.textSubtle, marginBottom:14, marginTop:-6}}>
                  Aapke number pe call aayegi jo 6-digit OTP bolegi. No documents/DLT needed.
                </div>
                <button style={btnStyle} onClick={sendPhoneOtpSms} disabled={loading}>
                  {loading ? 'Calling...' : '📞 Call me with OTP →'}
                </button>
              </>
            )}

            {signupMethod === 'sms' && phoneStep === 'enter-otp' && (
              <>
                <label style={labelStyle}>Enter OTP heard on call to +91-{form.ownerMobile}</label>
                <input style={{...inputStyle, fontSize:'1.2rem', letterSpacing:'0.4em', textAlign:'center', fontFamily:'monospace'}}
                  value={phoneOtp} onChange={e => setPhoneOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="0000" maxLength={6} />
                <button style={btnStyle} onClick={verifyPhoneOtpSms} disabled={loading}>
                  Continue →
                </button>
                <button onClick={() => { setPhoneStep('enter-phone'); setPhoneOtp(''); }}
                  style={{width:'100%', background:'none', border:'none', color:theme.textMuted, marginTop:10, cursor:'pointer', fontSize:'0.82rem'}}>
                  ← Change number
                </button>
              </>
            )}

            {signupMethod === 'sms' && phoneStep === 'details' && (
              <>
                <div style={{background:'#E4FFE4', padding:'10px 12px', borderRadius:8, marginBottom:14, fontSize:'0.82rem', color:'#0A7B3D', fontWeight:500}}>
                  ✓ Mobile: +91-{form.ownerMobile} (Voice OTP entered)
                </div>
                <label style={labelStyle}>Clinic Name *</label>
                <input style={inputStyle} value={form.clinicName} onChange={e => update('clinicName', e.target.value)} placeholder="e.g. Sharma Dental Hospital" />
                <label style={labelStyle}>Owner / Admin Name *</label>
                <input style={inputStyle} value={form.ownerName} onChange={e => update('ownerName', e.target.value)} placeholder="Dr. Rajesh Sharma" />
                <label style={labelStyle}>Email *</label>
                <input style={inputStyle} type="email" value={form.ownerEmail} onChange={e => update('ownerEmail', e.target.value)} placeholder="rajesh@sharmadental.com" />
                <label style={labelStyle}>Password * (min 8 chars)</label>
                <input style={inputStyle} type="password" value={form.password} onChange={e => update('password', e.target.value)} />
                <label style={labelStyle}>Confirm Password *</label>
                <input style={inputStyle} type="password" value={form.confirmPassword} onChange={e => update('confirmPassword', e.target.value)} />
                <button style={btnStyle} onClick={submitPhoneSignup} disabled={loading}>
                  {loading ? 'Creating...' : 'Verify OTP & Create Account'}
                </button>
              </>
            )}

            {/* WhatsApp signup (existing flow) */}
            {signupMethod === 'whatsapp' && (
              <>
                <label style={labelStyle}>Clinic Name *</label>
                <input style={inputStyle} value={form.clinicName} onChange={e => update('clinicName', e.target.value)} placeholder="e.g. Sharma Dental Hospital" />

                <label style={labelStyle}>Owner / Admin Name *</label>
                <input style={inputStyle} value={form.ownerName} onChange={e => update('ownerName', e.target.value)} placeholder="Dr. Rajesh Sharma" />

                <label style={labelStyle}>Email *</label>
                <input style={inputStyle} type="email" value={form.ownerEmail} onChange={e => update('ownerEmail', e.target.value)} placeholder="rajesh@sharmadental.com" />

                <label style={labelStyle}>Mobile Number * (10 digit)</label>
                <input style={inputStyle} value={form.ownerMobile} onChange={e => update('ownerMobile', e.target.value)} placeholder="9988776655" maxLength={10} />

                <label style={labelStyle}>Password * (min 8 chars)</label>
                <input style={inputStyle} type="password" value={form.password} onChange={e => update('password', e.target.value)} />

                <label style={labelStyle}>Confirm Password *</label>
                <input style={inputStyle} type="password" value={form.confirmPassword} onChange={e => update('confirmPassword', e.target.value)} />

                <button style={btnStyle} onClick={submitSignup} disabled={loading}>
                  {loading ? 'Loading...' : 'Create Account & Start Free Trial'}
                </button>
              </>
            )}

            <div style={{ textAlign: 'center', marginTop: '16px', fontSize: '0.85rem', color: theme.textSubtle }}>
              Already have account?{' '}
              <a onClick={onBackToLogin} style={{ color: OZ_BLUE, fontWeight: '600', cursor: 'pointer' }}>Sign In</a>
            </div>
          </>
        )}

        {step === 'verify' && signupResult && (
          <>
            <div style={{
              background: '#FFF6EC', padding: '14px', borderRadius: '8px', marginBottom: '20px',
              border: `1px solid ${OZ_ORANGE}40`,
            }}>
              <div style={{ fontSize: '0.82rem', color: theme.textSubtle }}>Aapka Clinic Code:</div>
              <div style={{ fontSize: '1.3rem', fontWeight: '800', color: OZ_ORANGE, letterSpacing: '0.1em' }}>
                {signupResult.clinicCode}
              </div>
              <div style={{ fontSize: '0.75rem', color: theme.textSubtle, marginTop: '4px' }}>
                Ye code note karke rakho — mobile app login ke liye chahiye hoga
              </div>
            </div>

            {signupResult.needsMobileVerify && (
              <div style={{ marginBottom: '20px', opacity: mobileVerified ? 0.6 : 1 }}>
                <label style={labelStyle}>
                  📱 Mobile OTP {mobileVerified && '✅ Verified'}
                </label>
                <div style={{ fontSize: '0.78rem', color: theme.textSubtle, marginBottom: '8px' }}>
                  WhatsApp pe 6-digit OTP aaya hoga {form.ownerMobile} par
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input style={{ ...inputStyle, marginBottom: 0 }} value={mobileOtp} onChange={e => setMobileOtp(e.target.value)} disabled={mobileVerified} maxLength={6} placeholder="6-digit OTP" />
                  <button onClick={verifyMobile} disabled={loading || mobileVerified} style={{
                    padding: '0 18px', background: mobileVerified ? '#D4E6D4' : OZ_BLUE,
                    color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: mobileVerified ? 'default' : 'pointer', whiteSpace: 'nowrap',
                  }}>{mobileVerified ? '✓' : 'Verify'}</button>
                </div>
                {!mobileVerified && (
                  <button onClick={() => resendOtp('mobile')} disabled={mobileResendIn > 0}
                    style={{ marginTop: '8px', background: 'none', border: 'none', color: OZ_BLUE, fontSize: '0.8rem', cursor: mobileResendIn > 0 ? 'default' : 'pointer', padding: 0 }}>
                    {mobileResendIn > 0 ? `Resend in ${mobileResendIn}s` : '↻ Resend OTP'}
                  </button>
                )}
              </div>
            )}

            {signupResult.needsEmailVerify && (
              <div style={{ marginBottom: '20px', opacity: emailVerified ? 0.6 : 1 }}>
                <label style={labelStyle}>
                  📧 Email OTP {emailVerified && '✅ Verified'}
                </label>
                <div style={{ fontSize: '0.78rem', color: theme.textSubtle, marginBottom: '8px' }}>
                  Email pe 6-digit OTP aaya hoga {form.ownerEmail} par
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input style={{ ...inputStyle, marginBottom: 0 }} value={emailOtp} onChange={e => setEmailOtp(e.target.value)} disabled={emailVerified} maxLength={6} placeholder="6-digit OTP" />
                  <button onClick={verifyEmail} disabled={loading || emailVerified} style={{
                    padding: '0 18px', background: emailVerified ? '#D4E6D4' : OZ_BLUE,
                    color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: emailVerified ? 'default' : 'pointer', whiteSpace: 'nowrap',
                  }}>{emailVerified ? '✓' : 'Verify'}</button>
                </div>
                {!emailVerified && (
                  <button onClick={() => resendOtp('email')} disabled={emailResendIn > 0}
                    style={{ marginTop: '8px', background: 'none', border: 'none', color: OZ_BLUE, fontSize: '0.8rem', cursor: emailResendIn > 0 ? 'default' : 'pointer', padding: 0 }}>
                    {emailResendIn > 0 ? `Resend in ${emailResendIn}s` : '↻ Resend OTP'}
                  </button>
                )}
              </div>
            )}

            {(mobileVerified || !signupResult.needsMobileVerify) && (emailVerified || !signupResult.needsEmailVerify) && (
              <button style={btnStyle} onClick={finishSignup}>
                Login Page Pe Jao →
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ClinicSignupPage;
